/**
 * Pure data provider for the four task renderer plugins
 * (`column.task`, `timeline[row].task`, `timeline[column].task`,
 * `grid.task`).
 *
 * Walks the row tree to find a `SourceRow` with `TASKS_FACADE`
 * attached (typically the `:~lona:tasks` constituent under a
 * task-list alias) and returns raw `TaskCellValue` values whose
 * `scheduledStart` / `dueDate` overlaps the requested range.
 *
 * Both DOM (`TasksPlugin`) and CLI (`renderSheetPlans`) consume this
 * so task fixed-child intent stays aligned across adapters.
 */

import type { Option } from "../core/option.ts";
import { NaiveDate } from "@tento-chrono";

import type { TaskCellValue } from "../core/dtype.ts";
import { TASKS_FACADE } from "../facades/tasks-facade.ts";
import type {
  FixedChildIntent,
  PluginDataProviders,
  TimelineItemProvider,
} from "./plugin-providers.ts";
import type { ModelRow } from "../rows/model-row.ts";
import { ListDataSource } from "../rows/variants/list-data-source.ts";
import { SourceRow } from "../rows/variants/source-row.ts";

const PLUGIN_ID = "column.task";

/**
 * Pick the date range for a task. Prefers `scheduledStart`/`scheduledEnd`,
 * falls back to `dueDate`. A task with no temporal hook returns null
 * — the caller should skip it.
 */
function taskDateRange(task: TaskCellValue): Option<NaiveDate.Range> {
  if (task.scheduledStart && task.scheduledEnd) {
    return new NaiveDate.Range(
      NaiveDate.fromDse(task.scheduledStart.dse),
      NaiveDate.fromDse(task.scheduledEnd.dse),
    );
  }
  if (task.scheduledStart) {
    const date = NaiveDate.fromDse(task.scheduledStart.dse);
    return new NaiveDate.Range(date, date);
  }
  if (task.dueDate) {
    const date = NaiveDate.fromDse(task.dueDate.dse);
    return new NaiveDate.Range(date, date);
  }
  return null;
}

function rangesOverlap(a: NaiveDate.Range, b: NaiveDate.Range): boolean {
  return a.start.dse <= b.end.dse && b.start.dse <= a.end.dse;
}

/**
 * Walk the row tree and collect every Task carried by attached
 * facades on writable source rows. `ListDataSource` aggregates are
 * walked through their `constituentRows()` (which filter renderer
 * children) so per-list provider sources deeper in the subtree get
 * visited.
 */
function collectTasks(row: ModelRow): readonly TaskCellValue[] {
  if (row instanceof ListDataSource) {
    const tasks: TaskCellValue[] = [];
    for (const constituent of row.constituentRows()) {
      tasks.push(...collectTasks(constituent));
    }
    return tasks;
  }
  if (row instanceof SourceRow) {
    const facade = row.as(TASKS_FACADE);
    if (facade) return facade.peekAll();
  }
  const tasks: TaskCellValue[] = [];
  for (const child of row.children) {
    tasks.push(...collectTasks(child));
  }
  return tasks;
}

/**
 * The data provider behind the four task renderers (`column.task`, `grid.task` and the two timeline
 * ones): the tasks under a row in a range.
 */
export class TasksDataProvider implements TimelineItemProvider<TaskCellValue> {
  readonly pluginId: "column.task" = PLUGIN_ID;

  // ── TimelineItemProvider ───────────────────────────────────────────

  items(row: ModelRow, range: NaiveDate.Range): readonly TaskCellValue[] {
    return collectTasks(row).filter((task) => {
      const r = taskDateRange(task);
      return r ? rangesOverlap(r, range) : false;
    });
  }

  dateRange(task: TaskCellValue): NaiveDate.Range {
    const r = taskDateRange(task);
    if (r) return r;
    // Defensive: items() filters undated tasks out, so this branch
    // is only reachable if a caller passes a task we never returned.
    const today = NaiveDate.today();
    return new NaiveDate.Range(today, today);
  }

  itemId(task: TaskCellValue): string {
    return task.id;
  }

  // ── Pure FixedChildIntent producer ────────────────────────────────

  fixedChildren(
    row: ModelRow,
    range: NaiveDate.Range,
  ): readonly FixedChildIntent[] {
    return this.items(row, range).map((task) => ({
      id: task.id,
      range: this.dateRange(task),
      align: "start",
      fillWidth: false,
      isOverflow: false,
      pluginId: PLUGIN_ID,
    }));
  }
}

/** `dataProvider` factory for the catalog. */
export function tasksDataProvider(): PluginDataProviders<TaskCellValue, never> {
  const provider = new TasksDataProvider();
  return {
    timelineItems: provider,
    fixedChildren: (row, range) => provider.fixedChildren(row, range),
  };
}
